import { getLoginDataFromStorage } from '@shared/utils/loginUtils';
import { Notification } from './types';

/**
 * Derives the WebSocket notification URL from the existing
 * VITE_NOTIFICATION_API_ENDPOINT environment variable.
 *
 * Pattern:
 *   https://api-notification-staging.bynry.com/api/notification/
 *     → wss://api-notification-staging.bynry.com/ws/notifications/?remote_utility_id=X&consumer_no=Y
 */
export const getNotificationWsUrl = (): string | null => {
  const httpEndpoint = import.meta.env.VITE_NOTIFICATION_API_ENDPOINT;

  if (!httpEndpoint) {
    console.error('[Notification WS] VITE_NOTIFICATION_API_ENDPOINT is not configured');
    return null;
  }

  const { remoteUtilityId, remoteConsumerNumber } = getLoginDataFromStorage();

  if (!remoteUtilityId || !remoteConsumerNumber) {
    console.warn('[Notification WS] Missing login data (remoteUtilityId or consumerNo)');
    return null;
  }

  try {
    const url = new URL(httpEndpoint);

    // https → wss, http → ws
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';

    // Replace the REST path with the WebSocket path
    url.pathname = '/ws/notifications/';

    // Append query params
    url.searchParams.set('remote_utility_id', remoteUtilityId);
    url.searchParams.set('consumer_no', remoteConsumerNumber);

    return url.toString();
  } catch (error) {
    console.error('[Notification WS] Failed to build WebSocket URL:', error);
    return null;
  }
};

// ── Display helpers ─────────────────────────────────────────────────

/** Map action_type → human-readable title */
const ACTION_TYPE_LABELS: Record<string, string> = {
  service_created: 'Service Request Created',
  service_in_progress: 'Service Request In Progress',
  service_completed: 'Service Request Completed',
  service_closed: 'Service Request Closed',
  complaint_created: 'Complaint Registered',
  complaint_in_progress: 'Complaint In Progress',
  complaint_resolved: 'Complaint Resolved',
  complaint_closed: 'Complaint Closed',
  disconnection_created: 'Disconnection Request Created',
  disconnection_in_progress: 'Disconnection In Progress',
  disconnection_completed: 'Disconnection Completed',
  reconnection_created: 'Reconnection Request Created',
  reconnection_in_progress: 'Reconnection In Progress',
  reconnection_completed: 'Reconnection Completed',
  transfer_created: 'Transfer Request Created',
  transfer_in_progress: 'Transfer In Progress',
  transfer_completed: 'Transfer Completed',
  payment_received: 'Payment Received',
  bill_generated: 'Bill Generated',
};

/** Get a human-readable title from the notification */
export const getNotificationTitle = (n: Notification): string => {
  return ACTION_TYPE_LABELS[n.action_type] || formatActionType(n.action_type);
};

/** Fallback: convert snake_case action_type to Title Case */
const formatActionType = (actionType: string): string => {
  if (!actionType) return 'Notification';
  return actionType
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/** Build a description string from the notification metadata */
export const getNotificationDescription = (n: Notification): string => {
  const meta = n.metadata;
  if (!meta) return n.message || '';

  const parts: string[] = [];

  // Entity type + request number
  if (meta.entity && meta.request_no) {
    parts.push(`${meta.entity} ${meta.request_no}`);
  }

  // Service/complaint name from utility_support_request_details
  const serviceName = meta.utility_support_request_details?.name;
  if (serviceName) {
    parts.push(`(${serviceName})`);
  }

  // Consumer info
  if (meta.first_name || meta.last_name) {
    parts.push(`for ${meta.first_name || ''} ${meta.last_name || ''}`.trim());
  }

  if (meta.consumer_no) {
    parts.push(`• Account: ${meta.consumer_no}`);
  }

  return parts.join(' ') || n.message || '';
};

/** Get the category from metadata.entity (Service, Complaint, etc.) */
export const getNotificationCategory = (n: Notification): string => {
  return n.metadata?.entity || n.metadata?.request_type || 'General';
};

/** Get priority/severity from event_data */
export const getNotificationSeverity = (n: Notification): string => {
  return n.event_data?.severity || 'info';
};

/** Check if a notification is unread (status === 0 means Pending/unread) */
export const isNotificationUnread = (n: Notification): boolean => {
  return n.status === 0;
};
